'use client';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CustomField } from '@/components/form/manual-fields/custom-fields/custom-field';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TextArea } from '@/components/ui/textarea';
import { cn } from '@/lib/cn';
import { useMemo, useState } from 'react';
import type { Control, UseFormSetValue } from 'react-hook-form';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { FileDropZone } from '../../../components/file-drop-zone';
import { FileRow } from '../../../components/file-row';
import type {
  ContextCustomFieldInclusion,
  ErrandContextResponse,
  ErrandTypeContext,
} from '../../[id]/hooks/use-get-errand-context';
import {
  isAllowedForAnonymous,
  isRequiredForAnonymous,
} from '../../[id]/hooks/use-get-errand-context';
import { cleanFileName } from '../../[id]/utils/format';
import type {
  CreateErrandFileFormValue,
  CreateErrandFormValues,
} from '../types';
import { processFileForUpload } from '../utils/process-file';
import { Icons } from '@/components/icons';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
const MAX_FILE_COUNT = 10;
const ACCEPTED_FILE_TYPES = 'image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt';

const OBJECT_ACCESS_LABELS: Record<number, string> = {
  0: 'Nyckel i tub',
  1: 'Huvudnyckel',
  2: 'Ring / Kontakta före',
  3: 'Hyresgäst öppnar',
  4: 'Nyckel överlämnad',
};

type ContextAddress = ErrandContextResponse['addresses'][number];

interface ErrandDetailsStepProps {
  readonly config: ErrandTypeContext;
  readonly contextData: ErrandContextResponse;
  readonly addresses: ContextAddress[];
  readonly selectedAddressId: string | null;
  readonly showAddress: boolean;
  readonly isAddressRequired: boolean;
  readonly customFieldInclusions: ContextCustomFieldInclusion[];
  readonly onSelectAddress: (id: string | null) => void;
  readonly isSubmitting: boolean;
  readonly submitError: Record<string, unknown> | null;
  readonly onSubmit: (values: CreateErrandFormValues) => void;
  readonly onBack: () => void;
}

export function ErrandDetailsStep({
  config,
  contextData,
  addresses,
  selectedAddressId,
  showAddress,
  isAddressRequired,
  customFieldInclusions,
  onSelectAddress,
  isSubmitting,
  submitError,
  onSubmit,
  onBack,
}: ErrandDetailsStepProps) {
  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    formState: { errors },
  } = useForm<CreateErrandFormValues>({
    defaultValues: {
      title: '',
      description: '',
      has_pets: null,
      object_access: null,
      reporter_first_name: '',
      reporter_last_name: '',
      reporter_email: '',
      reporter_phone_number: '',
      reporter_ssn: '',
      reporter_org_no: '',
      custom_field_values: {},
      errand_files: [],
    },
  });
  const [customFieldErrors, setCustomFieldErrors] = useState<
    Record<string, string>
  >({});

  const customFieldValues =
    useWatch({ control, name: 'custom_field_values' }) ?? {};

  const showDescription = isAllowedForAnonymous(
    config.description_configuration
  );
  const requireDescription = isRequiredForAnonymous(
    config.description_configuration
  );

  const showHasPets = isAllowedForAnonymous(config.has_pets_configuration);
  const showObjectAccess = isAllowedForAnonymous(
    config.object_access_configuration
  );
  const requireObjectAccess = isRequiredForAnonymous(
    config.object_access_configuration
  );

  const showFiles =
    isAllowedForAnonymous(config.images_configuration) ||
    isAllowedForAnonymous(config.files_configuration);

  const reporterFields = {
    first_name: {
      show: isAllowedForAnonymous(
        config.temporary_contact_first_name_configuration
      ),
      required: isRequiredForAnonymous(
        config.temporary_contact_first_name_configuration
      ),
      label: 'Förnamn',
      key: 'reporter_first_name' as const,
    },
    last_name: {
      show: isAllowedForAnonymous(
        config.temporary_contact_last_name_configuration
      ),
      required: isRequiredForAnonymous(
        config.temporary_contact_last_name_configuration
      ),
      label: 'Efternamn',
      key: 'reporter_last_name' as const,
    },
    email: {
      show: isAllowedForAnonymous(config.temporary_contact_email_configuration),
      required: isRequiredForAnonymous(
        config.temporary_contact_email_configuration
      ),
      label: 'E-post',
      key: 'reporter_email' as const,
    },
    phone_number: {
      show: isAllowedForAnonymous(
        config.temporary_contact_phone_number_configuration
      ),
      required: isRequiredForAnonymous(
        config.temporary_contact_phone_number_configuration
      ),
      label: 'Telefonnummer',
      key: 'reporter_phone_number' as const,
    },
    ssn: {
      show: isAllowedForAnonymous(config.temporary_contact_ssn_configuration),
      required: isRequiredForAnonymous(
        config.temporary_contact_ssn_configuration
      ),
      label: 'Personnummer',
      key: 'reporter_ssn' as const,
    },
    org_no: {
      show: isAllowedForAnonymous(
        config.temporary_contact_org_no_configuration
      ),
      required: isRequiredForAnonymous(
        config.temporary_contact_org_no_configuration
      ),
      label: 'Organisationsnummer',
      key: 'reporter_org_no' as const,
    },
  };

  const hasAnyReporterField = Object.values(reporterFields).some((f) => f.show);

  const hasAnyIdentifierField =
    reporterFields.email.show ||
    reporterFields.phone_number.show ||
    reporterFields.ssn.show ||
    reporterFields.org_no.show;

  const defaultDescriptions =
    config.show_default_descriptions && config.default_descriptions
      ? contextData.default_descriptions.filter((d) =>
          config.default_descriptions.includes(d.id)
        )
      : [];

  const objectAccessOptions = config.object_access_available_options ?? [];

  function onFormSubmit(values: CreateErrandFormValues): void {
    if (requireObjectAccess && values.object_access == null) {
      setError('object_access', { message: 'Tillträde krävs' });
      return;
    }

    const nextCustomFieldErrors = customFieldInclusions.reduce<
      Record<string, string>
    >((acc, inclusion) => {
      const fieldType = inclusion.field_type;
      const value = values.custom_field_values[fieldType.id] ?? '';
      if (fieldType.required && value.trim() === '') {
        acc[fieldType.id] = `${fieldType.title} krävs`;
      }
      return acc;
    }, {});

    if (Object.keys(nextCustomFieldErrors).length > 0) {
      setCustomFieldErrors(nextCustomFieldErrors);
      return;
    }

    setCustomFieldErrors({});
    onSubmit(values);
  }

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className='space-y-6'>
      <div>
        <h2 className='text-lg font-semibold'>Detaljer</h2>
        <p className='text-sm text-muted-foreground'>
          Fyll i informationen nedan för att skapa ärendet.
        </p>
      </div>

      {/* Address */}
      {showAddress && (
        <AddressField
          addresses={addresses}
          selectedAddressId={selectedAddressId}
          isRequired={isAddressRequired}
          onSelect={onSelectAddress}
        />
      )}

      {/* Title — always required */}
      <div className='space-y-2'>
        <Label htmlFor='title'>
          Rubrik <span className='text-destructive'>*</span>
        </Label>
        <Input
          id='title'
          {...register('title', { required: 'Rubrik krävs' })}
          placeholder='Beskriv ärendet kortfattat'
        />
        {errors.title && (
          <p className='text-sm text-destructive'>{errors.title.message}</p>
        )}
      </div>

      {/* Description */}
      {showDescription && (
        <div className='space-y-2'>
          <Label htmlFor='description'>
            Beskrivning{' '}
            {requireDescription && <span className='text-destructive'>*</span>}
          </Label>
          {defaultDescriptions.length > 0 && (
            <Select
              onValueChange={(value) => {
                const desc = defaultDescriptions.find((d) => d.id === value);
                if (desc) {
                  setValue('description', desc.description, {
                    shouldDirty: true,
                  });
                }
              }}
            >
              <SelectTrigger className='mb-2'>
                <SelectValue placeholder='Välj en standardbeskrivning...' />
              </SelectTrigger>
              <SelectContent>
                {defaultDescriptions.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <TextArea
            id='description'
            {...register('description', {
              required: requireDescription ? 'Beskrivning krävs' : false,
            })}
            placeholder='Beskriv ärendet i detalj'
            rows={4}
          />
          {errors.description && (
            <p className='text-sm text-destructive'>
              {errors.description.message}
            </p>
          )}
        </div>
      )}

      {/* Has pets */}
      {showHasPets && (
        <div className='flex items-center gap-3'>
          <Checkbox
            id='has_pets'
            onCheckedChange={(checked) =>
              setValue('has_pets', checked === true, { shouldDirty: true })
            }
          />
          <Label htmlFor='has_pets' className='cursor-pointer'>
            Det finns husdjur
          </Label>
        </div>
      )}

      {/* Object access */}
      {showObjectAccess && objectAccessOptions.length > 0 && (
        <div className='space-y-2'>
          <Label htmlFor='object_access'>
            Tillträde{' '}
            {requireObjectAccess && <span className='text-destructive'>*</span>}
          </Label>
          <Select
            onValueChange={(value) =>
              setValue('object_access', Number(value), { shouldDirty: true })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder='Välj tillträdesmetod...' />
            </SelectTrigger>
            <SelectContent>
              {objectAccessOptions.map((opt) => (
                <SelectItem key={opt} value={String(opt)}>
                  {OBJECT_ACCESS_LABELS[opt] ?? `Alternativ ${opt}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.object_access && (
            <p className='text-sm text-destructive'>
              {errors.object_access.message}
            </p>
          )}
        </div>
      )}

      {/* Custom fields */}
      {customFieldInclusions.length > 0 && (
        <div className='space-y-4'>
          <h3 className='text-base font-medium'>Övriga fält</h3>
          <div className='space-y-4'>
            {customFieldInclusions.map((inclusion) => {
              const fieldType = inclusion.field_type;
              return (
                <CustomField
                  key={inclusion.id}
                  kind={fieldType.kind}
                  title={fieldType.title}
                  description={fieldType.description}
                  required={fieldType.required}
                  options={fieldType.options}
                  value={customFieldValues[fieldType.id] ?? ''}
                  error={customFieldErrors[fieldType.id]}
                  onValueChange={(value) => {
                    setValue(
                      'custom_field_values',
                      {
                        ...customFieldValues,
                        [fieldType.id]: value,
                      },
                      { shouldDirty: true }
                    );
                    setCustomFieldErrors((prev) => {
                      if (!prev[fieldType.id]) return prev;
                      const next = { ...prev };
                      delete next[fieldType.id];
                      return next;
                    });
                  }}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Reporter contact information */}
      {hasAnyReporterField && (
        <div className='space-y-4'>
          <h3 className='text-base font-medium'>Kontaktuppgifter</h3>
          {hasAnyIdentifierField && (
            <Alert variant='info'>
              <Icons.info className='size-4' />
              <AlertDescription>
                Matchar du e-post, telefonnummer, personnummer eller
                organisationsnummer mot dina befintliga uppgifter i systemet går
                ärendehanteringen snabbare.
              </AlertDescription>
            </Alert>
          )}

          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            {Object.values(reporterFields).map((field) => {
              if (!field.show) return null;
              return (
                <div key={field.key} className='space-y-2'>
                  <Label htmlFor={field.key}>
                    {field.label}{' '}
                    {field.required && (
                      <span className='text-destructive'>*</span>
                    )}
                  </Label>
                  <Input
                    id={field.key}
                    {...register(field.key, {
                      required: field.required ? `${field.label} krävs` : false,
                    })}
                  />
                  {errors[field.key] && (
                    <p className='text-sm text-destructive'>
                      {errors[field.key]?.message}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* File attachments */}
      {showFiles && <FilesField control={control} setValue={setValue} />}

      {/* Submit error */}
      {submitError && (
        <div className='rounded-md border border-destructive/50 bg-destructive/10 p-3'>
          <p className='text-sm font-medium text-destructive'>
            Något gick fel vid skapande av ärendet. Kontrollera formuläret och
            försök igen.
          </p>
          {typeof submitError === 'object' && (
            <pre className='mt-2 max-h-32 overflow-auto text-xs text-destructive/80'>
              {JSON.stringify(submitError, null, 2)}
            </pre>
          )}
        </div>
      )}

      {/* Actions */}
      <div className='flex items-center gap-3 pt-2'>
        <Button
          type='button'
          className='hover:bg-foreground/10'
          variant='outline'
          onClick={onBack}
        >
          Tillbaka
        </Button>
        <Button
          type='submit'
          className='border-foreground bg-foreground hover:bg-foreground/80'
          disabled={isSubmitting}
        >
          {isSubmitting && (
            <Icons.loader className='mr-2 size-4 animate-spin' />
          )}
          Skicka in
        </Button>
      </div>
    </form>
  );
}

function formatAddress(address: ContextAddress): string {
  const parts = [address.street, address.street_number].filter(Boolean);
  const street = parts.join(' ');
  const location = [address.postal_code, address.city]
    .filter(Boolean)
    .join(' ');
  return [street, location].filter(Boolean).join(', ');
}

function deduplicateAddresses(addresses: ContextAddress[]): ContextAddress[] {
  const seen = new Set<string>();
  return addresses.filter((a) => {
    const key = [a.street, a.street_number, a.postal_code, a.city]
      .map((v) => (v ?? '').trim().toLowerCase())
      .join('|');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

interface AddressFieldProps {
  readonly addresses: ContextAddress[];
  readonly selectedAddressId: string | null;
  readonly isRequired: boolean;
  readonly onSelect: (id: string | null) => void;
}

function AddressField({
  addresses,
  selectedAddressId,
  isRequired,
  onSelect,
}: AddressFieldProps) {
  const [isOpen, setIsOpen] = useState(false);
  const uniqueAddresses = useMemo(
    () => deduplicateAddresses(addresses),
    [addresses]
  );
  const selectedAddress = uniqueAddresses.find(
    (a) => a.id === selectedAddressId
  );

  return (
    <div className='space-y-2'>
      <Label>
        Adress {isRequired && <span className='text-destructive'>*</span>}
      </Label>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            role='combobox'
            aria-expanded={isOpen}
            className='w-full justify-between'
            type='button'
          >
            {selectedAddress ? (
              <span className='flex items-center gap-2 truncate'>
                <Icons.address className='size-4 shrink-0 text-muted-foreground' />
                {formatAddress(selectedAddress)}
              </span>
            ) : (
              <span className='text-muted-foreground'>Sök adress...</span>
            )}
            <Icons.selectIndicator className='ml-2 size-4 shrink-0 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-[var(--radix-popover-trigger-width)] p-0'>
          <Command
            filter={(value, search) => {
              if (!search) return 1;
              return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
            }}
          >
            <CommandInput placeholder='Sök på gata, stad eller postnummer...' />
            <CommandList>
              <CommandEmpty>Ingen adress hittades.</CommandEmpty>
              <CommandGroup>
                {uniqueAddresses.map((address) => (
                  <CommandItem
                    key={address.id}
                    value={formatAddress(address)}
                    onSelect={() => {
                      onSelect(address.id);
                      setIsOpen(false);
                    }}
                  >
                    <Icons.check
                      className={cn(
                        'mr-2 size-4',
                        selectedAddressId === address.id
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                    <div className='flex flex-col'>
                      <span className='text-sm'>
                        {address.street} {address.street_number}
                      </span>
                      <span className='text-xs text-muted-foreground'>
                        {address.postal_code} {address.city}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

interface FilesFieldProps {
  readonly control: Control<CreateErrandFormValues>;
  readonly setValue: UseFormSetValue<CreateErrandFormValues>;
}

function FilesField({ control, setValue }: FilesFieldProps) {
  const { fields, append } = useFieldArray({
    control,
    name: 'errand_files',
  });

  const fileValues = useWatch({ control, name: 'errand_files' });

  function handleRemoveFile(index: number) {
    const updated = (fileValues ?? []).filter((_, i) => i !== index);
    setValue('errand_files', updated, { shouldDirty: true });
  }

  async function addFiles(fileList: FileList) {
    const remaining = MAX_FILE_COUNT - fields.length;
    for (const file of Array.from(fileList).slice(0, remaining)) {
      if (file.size > MAX_FILE_SIZE) continue;
      const processed = await processFileForUpload(file);
      append({
        id: undefined,
        file: processed.file,
        preview_url: undefined,
        md5_hash: processed.md5_hash,
        processed_name: processed.name,
      });
    }
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-2'>
        <Icons.attachment className='size-4 text-muted-foreground' />
        <Label className='mb-0'>Bifoga filer</Label>
      </div>

      <FileDropZone
        label={
          fields.length === 0
            ? 'Dra och släpp eller klicka för att bifoga filer'
            : 'Dra och släpp eller klicka för att bifoga fler filer'
        }
        description={`Bilder, video, PDF, dokument (max ${MAX_FILE_COUNT} filer, ${MAX_FILE_SIZE / 1024 / 1024} MB per fil)`}
        accept={ACCEPTED_FILE_TYPES}
        onFiles={(files) => void addFiles(files)}
      />

      {fields.length > 0 && (
        <ul className='space-y-2'>
          {fields.map((field, index) => {
            const fv = field as CreateErrandFileFormValue & { id: string };
            const name =
              fv.file?.name ?? cleanFileName(fv.processed_name) ?? 'Okänd fil';
            return (
              <FileRow
                key={field.id}
                name={name}
                onRemove={() => handleRemoveFile(index)}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
}
